import {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import i18nInstance from '@/i18n/config';
import { branding } from '@/config/branding';
import {
  ArrowUp,
  X,
  Plus,
  MessageSquarePlus,
  ChevronDown,
  ChevronRight,
  Check,
  Paperclip,
  GalleryVerticalEnd,
  Reply,
  Star,
} from 'lucide-react';
import { Message } from '@/types';
import { Tooltip } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { apiUpload } from '@/lib/api';
import AgentIcon from '@/components/AgentIcon';
import { useFiles } from '@/contexts/FilesContext';
import ImageLightbox from '@/components/ImageLightbox';
import { useConversationsContext } from '@/contexts/ConversationsContext';
import { useConversationActions } from '@/hooks/conversation/useConversationActions';
import { showToast } from '@/components/Toast';
import { useProjects } from '@/hooks/project/useProjects';
import {
  useUIStore,
  useFilesStore,
  useConversationsStore,
  useProjectsStore,
} from '@/stores';
import DynamicControls from './ChatInput/DynamicControls';
import ConfirmDialog from '@/components/ConfirmDialog';
import { fetchModels, Model } from '@/services/modelService';
import SkillDropdown from '@/components/SkillDropdown';
import { AVAILABLE_SKILLS } from '@/stores/skillTaskStore';

// Force re-render on language change
const useForceUpdate = () => {
  const [, setForceUpdate] = useState(0);
  useEffect(() => {
    const onLanguageChanged = () => setForceUpdate(n => n + 1);
    i18nInstance.on('languageChanged', onLanguageChanged);
    return () => i18nInstance.off('languageChanged', onLanguageChanged);
  }, []);
};

// File tree node used for project file mentions
interface FileNode {
  id: string;
  name: string;
  type: 'file' | 'folder';
  folderId?: string | null;
  children?: FileNode[];
}

// Agent definition used for picker
interface Agent {
  id: string;
  name: string;
  logo?: string;
  description?: string;
}

// Config definitions for DynamicControls
interface ConfigOption {
  label: string;
  value: any;
}
interface ConfigItem {
  name: string;
  type: 'text' | 'radio' | 'checkbox' | 'select';
  default: any;
  description: string;
  options?: ConfigOption[];
}
interface ChatInputProps {
  onSendMessage: (
    content: string,
    isRetry?: boolean,
    options?: Record<string, any>
  ) => void;
  onStopRequest?: () => void;
  onNewConversation?: () => void;
  disabled?: boolean;
  isLoading?: boolean;
  placeholder?: string;
  centered?: boolean;
  currentView?: string;
  projectId?: string | null;
  projectName?: string | null;
  agentConfig?: ConfigItem[];
  conversationId?: string; // 用于判断是否为新对话
  conversationAgent?: Agent | null; // 当前对话的 agent（从对话消息中获取）
  onProjectClick?: (projectId: string) => void; // 点击项目标签时跳转到项目详情页
  isGroupChat?: boolean; // 是否为群聊会话
  // 🔥 群聊回复相关
  replyToMessage?: Message | null; // 要回复的消息
  onClearReply?: () => void; // 清除回复状态
  // 🔥 群聊 stop 功能
  onStopGroupChat?: () => void; // 中断群聊中所有正在进行的流式输出
  // 🔥 任务错误相关
  taskError?: string | null; // 任务错误信息（如 message_init 前返回错误）
  onClearTaskError?: () => void; // 清除任务错误
  // 🔥 Star/Unstar 功能
  isStarred?: boolean; // 当前对话是否已收藏
  onStarredChange?: (starred: boolean) => void; // 收藏状态变化回调
}
export default function ChatInput({
  onSendMessage,
  onStopRequest,
  onNewConversation,
  disabled = false,
  isLoading = false,
  placeholder = `Message ${branding.shortName}`,
  centered = false,
  currentView = 'chat',
  projectId = null,
  projectName = null,
  agentConfig = [],
  conversationId,
  conversationAgent = null,
  onProjectClick,
  isGroupChat = false,
  isStarred = false,
  onStarredChange,
  replyToMessage = null,
  onClearReply,
  onStopGroupChat,
  // taskError = null,
  // onClearTaskError,
}: ChatInputProps) {
  const { t } = useTranslation();
  useForceUpdate();
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [pastedImages, setPastedImages] = useState<
    Array<{
      id: string;
      name: string;
      file: File;
      fileId?: string;
      isUploading: boolean;
      error?: string;
      previewUrl?: string; // 缓存的预览 URL
    }>
  >([]);
  // 追踪已经开始上传的文件，防止重复上传
  const uploadingFilesRef = useRef<Set<File>>(new Set());
  // Preview for pasted images
  const [previewImage, setPreviewImage] = useState<{
    url: string;
    name: string;
  } | null>(null);
  // 从 store 读取 agent 配置值
  const agentConfigValues = useUIStore(state => state.agentConfigValues);
  const selectedAgent = useUIStore(state => state.selectedAgent);
  const setSelectedAgent = useUIStore(state => state.setSelectedAgent);
  const newChatAgent = useUIStore(state => state.newChatAgent);
  const setNewChatAgent = useUIStore(state => state.setNewChatAgent);
  const newChatProject = useUIStore(state => state.newChatProject);
  const setNewChatProject = useUIStore(state => state.setNewChatProject);
  const setAgentConfigValues = useUIStore(state => state.setAgentConfigValues);
  const agents = useUIStore(state => state.agents);
  const agentsLoaded = useUIStore(state => state.agentsLoaded);
  const defaultAgent = useUIStore(state => state.defaultAgent);
  const fetchAgents = useUIStore(state => state.fetchAgents);
  const conversationAgentMap = useUIStore(state => state.conversationAgentMap);
  const setConversationAgent = useUIStore(state => state.setConversationAgent);
  // 🔥 新增：草稿存储方法（用于刷新页面时保留用户输入）
  const setDraftMessage = useUIStore(state => state.setDraftMessage);
  const getDraftMessage = useUIStore(state => state.getDraftMessage);
  // 🔥 从 store 获取标记（用于防止草稿恢复覆盖清空的输入框）
  const sentFromNewChat = useUIStore(state => state.sentFromNewChat);
  const setSentFromNewChat = useUIStore(state => state.setSentFromNewChat);
  // 🔥 文件预览：用于在项目切换时关闭预览
  const setFilePreview = useUIStore(state => state.setFilePreview);
  // 🔥 Artifact 分屏渲染：用于在项目切换时关闭
  const setArtifactViewer = useUIStore(state => state.setArtifactViewer);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // @ Agent mention states
  const [showAgentDropdown, setShowAgentDropdown] = useState(false);
  const [agentQuery, setAgentQuery] = useState('');
  const [selectedAgentIndex, setSelectedAgentIndex] = useState(-1);

  // Model selection states
  const [models, setModels] = useState<Model[]>([]);
  const [selectedModel, setSelectedModel] = useState<string>('');
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const [modelsLoading, setModelsLoading] = useState(false);
  const modelButtonRef = useRef<HTMLButtonElement>(null);
  const modelListRef = useRef<HTMLDivElement>(null);

  // 🔥 /skill dropdown states
  const [showSkillDropdown, setShowSkillDropdown] = useState(false);
  const [skillQuery, setSkillQuery] = useState('');
  const [selectedSkillIndex, setSelectedSkillIndex] = useState(0);
  const [skillAnchorRect, setSkillAnchorRect] = useState<DOMRect | null>(null);
  const skillDropdownRef = useRef<HTMLDivElement>(null);

  // Handle model selection and save to backend
  const handleModelSelect = useCallback(async (modelName: string) => {
    setSelectedModel(modelName);
    setShowModelDropdown(false);

    // Save the selected model to backend (only send llmModel)
    try {
      const { updateUserSettings } = await import('@/services/userService');
      await updateUserSettings({
        llmModel: modelName,
      });
    } catch (error) {
      console.error('Failed to save model preference:', error);
    }
  }, []);
  // localSelectedAgent: 用户在当前会话中手动选择的 agent（仅用于新对话）
  const [localSelectedAgent, setLocalSelectedAgent] = useState<Agent | null>(
    null
  );
  const agentListRef = useRef<HTMLDivElement>(null);
  const agentButtonRef = useRef<HTMLButtonElement>(null);

  // Fetch models and user profile on component mount
  useEffect(() => {
    const loadModelsAndProfile = async () => {
      setModelsLoading(true);
      try {
        // Fetch models
        const fetchedModels = await fetchModels();
        setModels(fetchedModels);

        // Fetch user profile to get user's selected model
        const { fetchUserProfile } = await import('@/services/userService');
        const profile = await fetchUserProfile();

        // 优先使用用户选择的 llmModel，其次是系统的 defaultModel
        const userModel =
          profile?.settings?.llmModel || profile?.settings?.defaultModel;

        if (userModel) {
          // Check if user's model exists in the fetched models
          const modelExists = fetchedModels.some(m => m.name === userModel);
          if (modelExists) {
            setSelectedModel(userModel);
          } else if (fetchedModels.length > 0) {
            setSelectedModel(fetchedModels[0].name);
          }
        } else if (fetchedModels.length > 0 && !selectedModel) {
          setSelectedModel(fetchedModels[0].name);
        }
      } catch (error) {
        console.error('Failed to load models or profile:', error);
      } finally {
        setModelsLoading(false);
      }
    };

    loadModelsAndProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const attachmentMenuRef = useRef<HTMLDivElement>(null);
  const attachmentButtonRef = useRef<HTMLButtonElement>(null);
  const projectListRef = useRef<HTMLDivElement>(null);
  // \ mention states (using backslash for file mentions)
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(-1);
  const [scrollTop, setScrollTop] = useState(0);
  const mentionListRef = useRef<HTMLDivElement>(null);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [showProjectList, setShowProjectList] = useState(false);
  const [projectActionLoading, setProjectActionLoading] = useState(false);
  const [showRemoveProjectConfirm, setShowRemoveProjectConfirm] =
    useState(false);
  const [isRemovingProject, setIsRemovingProject] = useState(false);
  const [attachmentMenuPosition, setAttachmentMenuPosition] = useState({
    top: 0,
    left: 0,
  });
  const [attachmentMenuDirection, setAttachmentMenuDirection] = useState<
    'above' | 'below'
  >('below');
  const [pendingProject, setPendingProject] = useState<{
    id: string;
    name: string;
  } | null>(null);
  // 🔥 本地项目ID状态，用于确保 UI 立即响应项目变更（不依赖 prop 更新）
  const [localProjectId, setLocalProjectId] = useState<string | null>(
    projectId ?? null
  );
  const [showAllAgents, setShowAllAgents] = useState(false);
  // 🔥 @Agent 占位符状态
  const [showAtAgentPlaceholder, setShowAtAgentPlaceholder] = useState(false);
  const [atSymbolPosition, setAtSymbolPosition] = useState<number | null>(null);
  // Virtual scrolling constants
  const ITEM_HEIGHT = 40;
  const CONTAINER_HEIGHT = 200;
  const VISIBLE_ITEMS = Math.ceil(CONTAINER_HEIGHT / ITEM_HEIGHT);
  // Use global files state
  const { fileNames } = useFiles();
  const fileTree = useFilesStore(state =>
    projectId ? state.projectFilesCache[projectId]?.fileTree : undefined
  );
  const filesIsLoaded = useFilesStore(state =>
    projectId ? state.projectFilesCache[projectId]?.isLoaded : false
  );
  const projects = useProjectsStore(state => state.projects);
  const hasLoadedProjects = useProjectsStore(state => state.hasLoadedProjects);
  const { loadProjects } = useProjects();
  const updateConversationInList = useConversationsStore(
    state => state.updateConversationInList
  );
  const { setSidebarProjectConversationsCache } = useConversationsContext();
  const { moveSingleConversation, toggleStar } = useConversationActions({
    onCacheClear: () => setSidebarProjectConversationsCache({}),
  });

  // Generate all file paths from file tree
  const fileTreePaths = useMemo(() => {
    if (!projectId || !fileTree || fileTree.length === 0) return [];
    const paths: Array<{ path: string; type: 'file' | 'folder'; id: string }> =
      [];

    const traverse = (nodes: FileNode[], currentPath: string = '') => {
      for (const node of nodes) {
        const nodePath = currentPath
          ? `${currentPath}/${node.name}`
          : node.name;
        paths.push({ path: nodePath, type: node.type, id: node.id });
        if (node.children && node.children.length > 0) {
          traverse(node.children, nodePath);
        }
      }
    };

    traverse(fileTree);
    return paths;
  }, [projectId, fileTree]);

  // 🔥 同步 projectId prop 变化到本地状态
  useEffect(() => {
    setLocalProjectId(projectId ?? null);
  }, [projectId]);

  useEffect(() => {
    if (showProjectList && !hasLoadedProjects) {
      loadProjects();
    }
  }, [showProjectList, hasLoadedProjects, loadProjects]);

  // Load agents list on component mount - use centralized fetchAgents
  useEffect(() => {
    if (!agentsLoaded) {
      fetchAgents();
    }
  }, [agentsLoaded, fetchAgents]);

  // Handle click outside to close model dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Close model dropdown if clicking outside
      if (
        showModelDropdown &&
        !modelButtonRef.current?.contains(target) &&
        !modelListRef.current?.contains(target)
      ) {
        setShowModelDropdown(false);
      }
    };

    if (showModelDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showModelDropdown]);

  // 计算要显示的 agent（不再用复杂的同步逻辑）
  // 优先级：
  // - 已有对话：手动选择的agent > conversationAgent > selectedAgent > defaultAgent
  // - 新对话：localSelectedAgent > newChatAgent > selectedAgent > defaultAgent
  const displayAgent = useMemo(() => {
    if (conversationId) {
      // 已有对话
      // 优先使用手动选择的 agent（用户在历史对话中切换的）
      const manualAgent = conversationAgentMap[conversationId];
      if (manualAgent) {
        return manualAgent;
      }
      // 其次使用从消息中提取的 agent
      if (conversationAgent) {
        return conversationAgent;
      }
      // 🔥 修复：在 defaultAgent 之前使用 selectedAgent
      // 这样从其他页面进入时不会丢失当前选中的 agent
      return selectedAgent || defaultAgent;
    } else {
      // 新对话
      if (localSelectedAgent) {
        return localSelectedAgent;
      }
      return newChatAgent || selectedAgent || defaultAgent;
    }
  }, [
    conversationId,
    conversationAgent,
    localSelectedAgent,
    newChatAgent,
    selectedAgent,
    defaultAgent,
    conversationAgentMap,
  ]);

  // 当 conversationId 变化时，处理 agent 状态
  const prevConversationIdRef = useRef(conversationId);
  useEffect(() => {
    const wasNewChat = !prevConversationIdRef.current;
    const isNowExistingChat = !!conversationId;
    const isConversationChanged =
      prevConversationIdRef.current !== conversationId;
    prevConversationIdRef.current = conversationId;

    if (!isConversationChanged) {
      return;
    }

    // Case 1: 新对话刚获得 conversationId（发送消息后）
    // 把选中的 agent 迁移到 store 中的 conversationAgentMap
    if (wasNewChat && isNowExistingChat) {
      // 优先使用 localSelectedAgent，其次是 newChatAgent
      const agentToSave = localSelectedAgent || newChatAgent;
      if (agentToSave) {
        setConversationAgent(conversationId, agentToSave);
      }
      // 清除临时状态
      if (localSelectedAgent) {
        setLocalSelectedAgent(null);
      }
      if (newChatAgent) {
        setNewChatAgent(null);
      }
      return;
    }

    // Case 2: 切换到其他对话（不是新对话获得ID的情况）
    // 清除 localSelectedAgent（它只用于新对话）
    setLocalSelectedAgent(null);
  }, [
    conversationId,
    localSelectedAgent,
    newChatAgent,
    setNewChatAgent,
    setConversationAgent,
  ]);

  // 🔥 新增：草稿存储逻辑（用于刷新页面时保留用户输入）
  // 使用 useLayoutEffect 确保在浏览器绘制前同步执行，避免闪烁
  const prevConvIdForDraftRef = useRef<string | undefined>(undefined);
  useLayoutEffect(() => {
    // 🔥 首先检查：如果 sentFromNewChat 标记为 true，说明刚从新对话发送了消息
    // 需要清空输入框，不管是组件挂载还是 conversationId 变化
    if (sentFromNewChat) {
      setSentFromNewChat(false);
      setMessage('');
      // 清除该对话可能存在的草稿
      if (conversationId) {
        setDraftMessage(conversationId, '');
      }
      prevConvIdForDraftRef.current = conversationId;
      return;
    }

    const prevConvId = prevConvIdForDraftRef.current;
    prevConvIdForDraftRef.current = conversationId;

    // 🔥 如果是从 undefined 变成有值（新对话刚创建），不恢复草稿，并清空输入框
    if (!prevConvId && conversationId) {
      setMessage('');
      return;
    }

    // 🔥 修复：切换到另一个已存在的对话时，直接清空输入框
    // 不再恢复草稿，避免用户困惑
    if (conversationId && prevConvId && prevConvId !== conversationId) {
      setMessage('');
      // 同时清除新对话的草稿记录
      setDraftMessage(conversationId, '');
    }
  }, [
    conversationId,
    getDraftMessage,
    sentFromNewChat,
    setSentFromNewChat,
    setDraftMessage,
  ]);

  // 在 message 变化时保存草稿（使用 debounce 减少频率）
  // 🔥 修复：使用 ref 追踪上一个 conversationId，避免切换会话时将旧输入保存到新会话
  const prevConvIdForSaveRef = useRef(conversationId);
  useEffect(() => {
    if (!conversationId) return;

    // 🔥 修复：如果 conversationId 刚刚变化，只更新 ref，不保存草稿
    // 这样可以防止旧会话的输入被保存到新会话的草稿中
    if (prevConvIdForSaveRef.current !== conversationId) {
      prevConvIdForSaveRef.current = conversationId;
      return; // 跳过保存
    }

    // 使用 setTimeout 实现简单的 debounce
    const timeoutId = setTimeout(() => {
      if (message) {
        setDraftMessage(conversationId, message);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [message, conversationId, setDraftMessage]);

  // 当 newChatProject 存在时，初始化 pendingProject（用于新对话保留项目）
  useEffect(() => {
    if (newChatProject && !conversationId) {
      // 新对话且有保留的项目，设置 pendingProject
      setPendingProject(newChatProject);
      // 清除 newChatProject，避免重复设置
      setNewChatProject(null);
    } else if (conversationId && newChatProject) {
      // 对话已创建，清除 newChatProject
      setNewChatProject(null);
    }
  }, [newChatProject, conversationId, setNewChatProject]);

  const canChangeProject =
    !!conversationId && !conversationId.startsWith('temp-');

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      const maxHeight = 160;
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
      textarea.style.overflowY =
        textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
    }
  }, [message]);

  // Close agent dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        agentListRef.current &&
        !agentListRef.current.contains(event.target as Node) &&
        agentButtonRef.current &&
        !agentButtonRef.current.contains(event.target as Node)
      ) {
        setShowAgentDropdown(false);
        setAgentQuery('');
        setSelectedAgentIndex(-1);
        setShowAllAgents(false);
      }
      // Close model dropdown when clicking outside
      if (
        modelListRef.current &&
        !modelListRef.current.contains(event.target as Node) &&
        modelButtonRef.current &&
        !modelButtonRef.current.contains(event.target as Node)
      ) {
        setShowModelDropdown(false);
      }
    };
    if (showAgentDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAgentDropdown]);

  // Close mention dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        mentionListRef.current &&
        !mentionListRef.current.contains(event.target as Node) &&
        textareaRef.current &&
        !textareaRef.current.contains(event.target as Node)
      ) {
        setShowMentionDropdown(false);
        setMentionQuery('');
        setSelectedMentionIndex(-1);
      }
    };
    if (showMentionDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMentionDropdown]);

  // 🔥 Close skill dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        skillDropdownRef.current &&
        !skillDropdownRef.current.contains(event.target as Node) &&
        textareaRef.current &&
        !textareaRef.current.contains(event.target as Node)
      ) {
        setShowSkillDropdown(false);
        setSkillQuery('');
        setSelectedSkillIndex(0);
      }
    };
    if (showSkillDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSkillDropdown]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (showAttachmentMenu || showProjectList) {
        const clickedAttachmentMenu =
          attachmentMenuRef.current?.contains(target) ||
          attachmentButtonRef.current?.contains(target);
        const clickedProjectList = projectListRef.current?.contains(target);

        if (!clickedAttachmentMenu && !clickedProjectList) {
          setShowAttachmentMenu(false);
          setShowProjectList(false);
        }
      }
    };

    if (showAttachmentMenu || showProjectList) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAttachmentMenu, showProjectList]);
  // Filter files with substring and subsequence matching - return ALL matches
  const allFilteredFiles = useMemo(() => {
    // Use file tree paths if in a project, otherwise use old fileNames
    let allPaths: string[] = [];

    if (projectId && fileTreePaths.length > 0) {
      // Use file tree paths from the project
      allPaths = fileTreePaths.map(item => item.path);
    } else {
      // Fallback to old fileNames logic
      const filteredFiles: string[] = fileNames?.public?.slice() || [];
      if (projectId) {
        const projectFiles = fileNames?.[projectId] || [];
        filteredFiles.push(...projectFiles);
      } else if (fileNames) {
        Object.entries(fileNames).forEach(([projectId, projectFiles]) => {
          if (projectId !== 'public' && projectFiles?.length > 0) {
            filteredFiles.push(...projectFiles);
          }
        });
      }
      allPaths = filteredFiles;
    }

    if (!mentionQuery || mentionQuery.trim() === '') return allPaths; // Return all when no query

    const query = mentionQuery.toLowerCase();
    const substringMatches: string[] = [];
    const subsequenceMatches: string[] = [];

    allPaths.forEach(path => {
      const pathLower = path.toLowerCase();
      if (pathLower.includes(query)) {
        substringMatches.push(path);
      } else {
        // Check subsequence match
        let queryIndex = 0;
        for (
          let i = 0;
          i < pathLower.length && queryIndex < query.length;
          i++
        ) {
          if (pathLower[i] === query[queryIndex]) {
            queryIndex++;
          }
        }
        if (queryIndex === query.length) {
          subsequenceMatches.push(path);
        }
      }
    });
    // Substring matches first, then subsequence matches
    return [...substringMatches, ...subsequenceMatches];
  }, [fileNames, mentionQuery, projectId, fileTreePaths]);
  // Virtual scrolling calculations
  const virtualScrollData = useMemo(() => {
    const totalItems = allFilteredFiles.length;
    const totalHeight = totalItems * ITEM_HEIGHT;
    if (totalItems === 0) {
      return {
        totalHeight: 0,
        visibleItems: [],
        offsetY: 0,
        startIndex: 0,
        endIndex: 0,
      };
    }
    // Calculate visible range
    const startIndex = Math.max(0, Math.floor(scrollTop / ITEM_HEIGHT));
    const endIndex = Math.min(startIndex + VISIBLE_ITEMS + 2, totalItems); // +2 for buffer
    // Get visible items
    const visibleItems = allFilteredFiles.slice(startIndex, endIndex);
    // Calculate offset for positioning
    const offsetY = startIndex * ITEM_HEIGHT;
    return {
      totalHeight,
      visibleItems,
      offsetY,
      startIndex,
      endIndex,
    };
  }, [allFilteredFiles, scrollTop, ITEM_HEIGHT, VISIBLE_ITEMS]);
  // Reset scroll when query changes or dropdown opens
  useEffect(() => {
    if (showMentionDropdown) {
      setScrollTop(0);
      setSelectedMentionIndex(-1);
      // Ensure scroll container is reset
      setTimeout(() => {
        if (mentionListRef.current) {
          mentionListRef.current.scrollTop = 0;
        }
      }, 0);
    }
  }, [mentionQuery, showMentionDropdown]);
  // Handle scroll event for virtual scrolling
  const handleMentionScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      setScrollTop(target.scrollTop);
    },
    []
  );
  // Filter agents based on query
  const filteredAgents = useMemo(() => {
    if (!agentQuery) return agents;
    const query = agentQuery.toLowerCase();
    return agents.filter(
      agent =>
        agent.name.toLowerCase().includes(query) ||
        agent.description?.toLowerCase().includes(query)
    );
  }, [agents, agentQuery]);
  const dropdownAgents = useMemo(() => {
    if (showAllAgents) return filteredAgents;
    return filteredAgents.slice(0, 5);
  }, [filteredAgents, showAllAgents]);
  // Extract @mentions from message
  // const extractMentions = useCallback((text: string) => {
  //   const mentionRegex = /@\[([^\]]+)\]/g;
  //   const mentions: string[] = [];
  //   let match;
  //   while ((match = mentionRegex.exec(text)) !== null) {
  //     mentions.push(match[1]);
  //   }
  //   return mentions;
  // }, []);
  // 防抖 ref - 记录上次提交时间
  const lastSubmitTimeRef = useRef<number>(0);

  const handleSubmit = useCallback(() => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage) return;

    // 防抖：300ms 内重复点击会被忽略
    const now = Date.now();
    if (now - lastSubmitTimeRef.current < 300) {
      return;
    }
    lastSubmitTimeRef.current = now;

    // 🔥 检测 [skill:XXX] 格式，标记为 skill 任务（不拦截，走正常发送流程模拟流式返回）
    let isSkillTask = false;
    let matchedSkillForTask: { id: string; name: string } | null = null;
    const skillPattern = /\[skill:([^\]]+)\]/i;
    const skillMatch = trimmedMessage.match(skillPattern);
    if (skillMatch) {
      const skillId = skillMatch[1].trim();
      const found = AVAILABLE_SKILLS.find(
        s =>
          s.id.toLowerCase() === skillId.toLowerCase() ||
          s.name.toLowerCase() === `skill:${skillId.toLowerCase()}`
      );
      if (found) {
        isSkillTask = true;
        matchedSkillForTask = { id: found.id, name: found.name };
        // 🔥 不在这里添加任务，改为在模拟回复完成后由 useMessageOperations 添加
        // 通过 options.isSkillTask 标记传递给消息发送流程
      }
    }

    // Check if all images are uploaded
    const pendingImages = pastedImages.filter(
      img => img.isUploading || img.error
    );
    if (pendingImages.length > 0) {
      return; // Wait for images to complete
    }

    // 🔥 群聊回复：将回复内容包装为 *replay* 格式
    let finalMessage = trimmedMessage;
    if (replyToMessage && isGroupChat) {
      // 获取被回复消息的用户名和内容
      const replyUser = (replyToMessage as any).user;
      const replyUserName = replyUser?.name || replyUser?.email || 'User';
      const replyBlocks = replyToMessage.choices?.[0]?.blocks || [];
      let replyContent = replyBlocks
        .filter((block: any) => block.type === 'text')
        .map((block: any) => block.content)
        .join('\n')
        .trim();
      // 🔥 去掉嵌套的 *replay* 内容，只保留主消息部分
      const nestedReplayRegex = /^\*replay\*[^:]+:.+?\*replay\*\n?/;
      if (nestedReplayRegex.test(replyContent)) {
        replyContent = replyContent.replace(nestedReplayRegex, '').trim();
      }
      replyContent = replyContent.slice(0, 100); // 限制回复内容长度
      // 格式: *replay*用户名:内容*replay*
      finalMessage = `*replay*${replyUserName}:${replyContent}*replay*\n${trimmedMessage}`;
    }

    // Prepare options with agentConfigValues, clientId, and fileIds
    const fileIds = pastedImages
      .filter(img => img.fileId && !img.error)
      .map(img => img.fileId!);

    // Use displayAgent (computed from conversation/newChat agent)
    const options = {
      ...agentConfigValues,
      clientId: displayAgent?.id || null,
      fileIds: fileIds.length > 0 ? fileIds : undefined,
      // Pass pending project for new conversations
      projectId: !conversationId ? pendingProject?.id : undefined,
      // Pass selected model
      model: selectedModel || undefined,
      // 🔥 Skill task flag - 模拟发送，不调用真实 API
      ...(isSkillTask
        ? {
            isSkillTask: true,
            skillId: matchedSkillForTask?.id,
            skillName: matchedSkillForTask?.name,
          }
        : {}),
    };
    // Note: sessionId generation is now handled internally by the caller (ChatPage)
    // 🔥 如果是从新对话发送，标记一下，防止获得 conversationId 后草稿恢复
    if (!conversationId) {
      setSentFromNewChat(true);
    }
    // 🔥 先清空状态，再发送消息，确保在 conversationId 变化前状态已清空
    setMessage('');
    // 🔥 清除草稿记录
    if (conversationId) {
      setDraftMessage(conversationId, '');
    }
    setAttachments([]);
    setPastedImages([]);
    setShowMentionDropdown(false);
    setShowAgentDropdown(false);
    // 🔥 清除回复状态
    if (onClearReply) {
      onClearReply();
    }
    // 发送消息（这会触发 conversationId 变化）
    onSendMessage(finalMessage, false, options);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    message,
    pastedImages,
    onSendMessage,
    agentConfigValues,
    displayAgent,
    conversationId,
    pendingProject,
    replyToMessage,
    isGroupChat,
    onClearReply,
    setSentFromNewChat,
    setDraftMessage,
  ]);
  // Handle @ agent selection
  const handleAgentSelect = useCallback(
    async (agent: Agent) => {
      // 如果正在加载中，不允许切换
      if (isLoading) {
        return;
      }

      // 根据是新对话还是已有对话，使用不同的存储方式
      if (conversationId) {
        // 已有对话：存到 store 中的 conversationAgentMap
        setConversationAgent(conversationId, agent);
      } else {
        // 新对话：用 localSelectedAgent
        setLocalSelectedAgent(agent);
      }
      setShowAgentDropdown(false);
      setAgentQuery('');
      setSelectedAgentIndex(-1);

      const agentData = {
        id: agent.id,
        name: agent.name,
        logo: agent.logo,
        description: agent.description,
        config: [],
      };

      // Update selectedAgent in store
      setSelectedAgent(agentData);

      // If this is a new chat (no conversationId), also save to newChatAgent
      // so it's preserved when switching conversations
      if (!conversationId) {
        setNewChatAgent(agentData);
      }

      // Update agentConfigValues with default values
      const defaultConfigValues: Record<string, any> = {};
      setAgentConfigValues(defaultConfigValues);

      // Remove the @ from message
      const textarea = textareaRef.current;
      if (textarea) {
        const cursorPos = textarea.selectionStart;
        const textBefore = message.substring(0, cursorPos);
        const textAfter = message.substring(cursorPos);
        // Find the @ symbol position
        const atIndex = textBefore.lastIndexOf('@');
        if (atIndex !== -1) {
          const newTextBefore = textBefore.substring(0, atIndex);
          const newMessage = newTextBefore + textAfter;
          setMessage(newMessage);
          // Set cursor position
          setTimeout(() => {
            textarea.setSelectionRange(atIndex, atIndex);
            textarea.focus();
          }, 0);
        }
      }
    },
    [
      message,
      isLoading,
      conversationId,
      setSelectedAgent,
      setNewChatAgent,
      setAgentConfigValues,
      setConversationAgent,
    ]
  );
  // Handle \ mention selection
  const handleMentionSelect = useCallback(
    (fileName: string) => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      const cursorPos = textarea.selectionStart;
      const textBefore = message.substring(0, cursorPos);
      const textAfter = message.substring(cursorPos);
      // Find the @ symbol position
      const atIndex = textBefore.lastIndexOf('@');
      if (atIndex === -1) return;
      // Replace @query with @[fileName]
      const newTextBefore = textBefore.substring(0, atIndex);
      const newMessage = `${newTextBefore}@[${fileName}] ${textAfter}`;
      setMessage(newMessage);
      setShowMentionDropdown(false);
      setMentionQuery('');
      setSelectedMentionIndex(-1);
      // 🔥 清除 @Agent 占位符状态
      setShowAtAgentPlaceholder(false);
      setAtSymbolPosition(null);
      // Set cursor position after the mention
      setTimeout(() => {
        const newCursorPos = atIndex + fileName.length + 4; // @[fileName] + space
        textarea.setSelectionRange(newCursorPos, newCursorPos);
        textarea.focus();
      }, 0);
    },
    [message]
  );
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // 🔥 Handle /skill dropdown navigation
    if (showSkillDropdown && filteredSkillsForNav.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedSkillIndex(prev =>
          prev < filteredSkillsForNav.length - 1 ? prev + 1 : prev
        );
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedSkillIndex(prev => (prev > 0 ? prev - 1 : prev));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const skill = filteredSkillsForNav[selectedSkillIndex];
        if (skill) {
          handleSkillSelect(skill.id, skill.name);
        }
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowSkillDropdown(false);
        setSkillQuery('');
        setSelectedSkillIndex(0);
        return;
      }
    }

    // Handle backspace for @mention deletion (only when not actively typing in mention dropdown)
    if (e.key === 'Backspace') {
      const textarea = textareaRef.current;
      if (textarea) {
        const cursorPos = textarea.selectionStart;
        const textBefore = message.substring(0, cursorPos);
        // Check if cursor position has @mention pattern before it
        // Match both incomplete @filename and complete @[filename] patterns
        const incompleteMentionMatch = textBefore.match(/@([^@\s[\]]+)$/);
        const completeMentionMatch = textBefore.match(/@\[[^\]]+\]\s*$/); // Allow trailing spaces
        const mentionMatch = completeMentionMatch || incompleteMentionMatch;
        if (mentionMatch) {
          // Only delete entire mention if there's content after @
          e.preventDefault();
          // Remove the entire @mention pattern
          const mentionStart = cursorPos - mentionMatch[0].length;
          const textAfter = message.substring(cursorPos);
          const newMessage = message.substring(0, mentionStart) + textAfter;
          setMessage(newMessage);
          setShowMentionDropdown(false);
          setMentionQuery('');
          setSelectedMentionIndex(-1);
          // Set cursor position to where @mention started
          setTimeout(() => {
            textarea.setSelectionRange(mentionStart, mentionStart);
            textarea.focus();
          }, 0);
          return;
        }
      }
    }
    // Handle agent dropdown navigation
    if (showAgentDropdown) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedAgentIndex(prev => {
          const maxIndex = filteredAgents.length - 1;
          return prev < maxIndex ? prev + 1 : prev;
        });
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedAgentIndex(prev => (prev > 0 ? prev - 1 : prev));
        return;
      }
      if (e.key === 'Enter' && selectedAgentIndex >= 0) {
        e.preventDefault();
        handleAgentSelect(filteredAgents[selectedAgentIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowAgentDropdown(false);
        setAgentQuery('');
        setSelectedAgentIndex(-1);
        return;
      }
    }
    // Handle mention dropdown navigation
    if (showMentionDropdown) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedMentionIndex(prev => {
          const maxIndex = allFilteredFiles.length - 1;
          let newIndex = prev < maxIndex ? prev + 1 : prev;
          // Scroll to keep selected item in view
          if (mentionListRef.current) {
            const itemTop = newIndex * ITEM_HEIGHT;
            const itemBottom = itemTop + ITEM_HEIGHT;
            const scrollTop = mentionListRef.current.scrollTop;
            const scrollBottom = scrollTop + CONTAINER_HEIGHT;
            if (itemBottom > scrollBottom) {
              mentionListRef.current.scrollTop =
                itemTop - CONTAINER_HEIGHT + ITEM_HEIGHT;
            }
          }
          return newIndex;
        });
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedMentionIndex(prev => {
          const newIndex = prev > 0 ? prev - 1 : prev;
          // Scroll to keep selected item in view
          if (mentionListRef.current && newIndex < prev) {
            const itemTop = newIndex * ITEM_HEIGHT;
            if (itemTop < mentionListRef.current.scrollTop) {
              mentionListRef.current.scrollTop = itemTop;
            }
          }
          return newIndex;
        });
        return;
      }
      if (e.key === 'Enter' && selectedMentionIndex >= 0) {
        e.preventDefault();
        handleMentionSelect(allFilteredFiles[selectedMentionIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowMentionDropdown(false);
        setMentionQuery('');
        setSelectedMentionIndex(-1);
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();

      // 🔥 如果正在显示 @Agent 占位符，按 Enter 补全为 @Agent
      if (showAtAgentPlaceholder && atSymbolPosition !== null) {
        const textarea = textareaRef.current;
        if (textarea) {
          const textBefore = message.substring(0, atSymbolPosition);
          const textAfter = message.substring(atSymbolPosition);
          // 找到 @ 符号的位置
          const atIndex = textBefore.lastIndexOf('@');
          if (atIndex !== -1) {
            // 替换 @xxx 为 @Agent
            const beforeAt = textBefore.substring(0, atIndex);
            const newMessage = beforeAt + '@Agent ' + textAfter;
            setMessage(newMessage);
            // 移动光标到 @Agent 之后
            const newCursorPos = atIndex + 7; // "@Agent ".length = 7
            setTimeout(() => {
              textarea.setSelectionRange(newCursorPos, newCursorPos);
              textarea.focus();
            }, 0);
          }
        }
        setShowAtAgentPlaceholder(false);
        setAtSymbolPosition(null);
        // 🔥 同时关闭文件选择框
        setShowMentionDropdown(false);
        setMentionQuery('');
        setSelectedMentionIndex(-1);
        return; // 不发送消息
      }

      // 允许在流式输出时发送新消息（会加入队列）
      // 🔥 如果有附件上传错误，禁止发送
      if (pastedImages.some(img => img.error)) {
        return;
      }
      handleSubmit();
    }
  };
  const allowedImageMimes = useMemo(
    () => ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
    []
  );
  const allowedImageExts = useMemo(
    () => ['jpg', 'jpeg', 'png', 'gif', 'webp'],
    []
  );
  const isAllowedImage = useCallback(
    (file: File) => {
      const mime = (file.type || '').toLowerCase();
      const ext = (file.name.split('.').pop() || '').toLowerCase();
      return (
        (mime && allowedImageMimes.includes(mime)) ||
        (ext && allowedImageExts.includes(ext))
      );
    },
    [allowedImageExts, allowedImageMimes]
  );
  const uploadImageFile = useCallback(async (file: File, name?: string) => {
    // 防止重复上传同一个文件
    if (uploadingFilesRef.current.has(file)) {
      return;
    }
    uploadingFilesRef.current.add(file);

    const tempId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const previewUrl = URL.createObjectURL(file); // 创建预览 URL 并缓存
    const imageEntry = {
      id: tempId,
      name: name || file.name || `image-${Date.now()}`,
      file,
      isUploading: true,
      error: undefined as string | undefined,
      previewUrl,
    };

    setPastedImages(prev => [...prev, imageEntry]);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiUpload<{
        fileId: string;
        message?: string;
        code?: number;
      }>('/api/messages/files', formData, undefined, {
        skipErrorToast: true,
      });

      // 🔥 检查 code 是否为 200，以及是否有 fileId
      if (response?.code === 200 && response?.fileId) {
        setPastedImages(prev =>
          prev.map(img =>
            img.id === tempId
              ? { ...img, fileId: response.fileId, isUploading: false }
              : img
          )
        );
      } else {
        const errorMsg = response?.message || t('upload.uploadFailed');
        // 🔥 弹出错误提示
        showToast(errorMsg, 'error', { title: t('common.error') });
        setPastedImages(prev =>
          prev.map(img =>
            img.id === tempId
              ? { ...img, isUploading: false, error: errorMsg }
              : img
          )
        );
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Upload failed';
      setPastedImages(prev =>
        prev.map(img =>
          img.id === tempId
            ? { ...img, isUploading: false, error: errorMsg }
            : img
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      let hasInvalid = false;
      files.forEach(file => {
        if (isAllowedImage(file)) {
          uploadImageFile(file, file.name);
        } else {
          hasInvalid = true;
        }
      });
      if (hasInvalid) {
        showToast(t('upload.uploadFilesOnly'), 'error', {
          title: t('chat.imagesOnly'),
        });
      }
      setShowAttachmentMenu(false);
      setShowProjectList(false);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [isAllowedImage, uploadImageFile, t]
  );

  const handleUploadClick = useCallback(() => {
    if (disabled || isLoading) return;
    fileInputRef.current?.click();
    setShowAttachmentMenu(false);
    setShowProjectList(false);
  }, [disabled, isLoading]);

  const handleProjectSelect = useCallback(
    async (targetProjectId: string) => {
      // 🔥 切换项目时关闭文件预览和 Artifact 分屏
      setFilePreview(null);
      setArtifactViewer(null);

      // If we don't have a persisted conversation yet (new or temp), only mark locally
      if (!conversationId || conversationId.startsWith('temp-')) {
        const p = projects?.find(pj => pj.id === targetProjectId);
        setPendingProject(
          p
            ? { id: p.id, name: p.name }
            : { id: targetProjectId, name: targetProjectId }
        );
        // Ensure a non-'common' client is selected when using a project.
        try {
          const { ensureNonCommonSelected } = await import('@/lib/agentUtils');
          const res = ensureNonCommonSelected();
          if (res?.switched) {
            // localSelectedAgent has higher priority in new chat display;
            // clear it so the auto-switched agent is visible immediately.
            setLocalSelectedAgent(null);
          }
        } catch (e) {
          console.warn('Auto-switch agent on project select failed', e);
        }

        setShowAttachmentMenu(false);
        setShowProjectList(false);
        return;
      }

      if (!canChangeProject) {
        setShowAttachmentMenu(false);
        setShowProjectList(false);
        return;
      }

      setProjectActionLoading(true);
      // 🔥 使用单个对话接口 /ai/conversations/{conversation_id}/move
      const success = await moveSingleConversation(
        conversationId,
        targetProjectId
      );
      if (success) {
        setLocalProjectId(targetProjectId);
        // 🔥 同时设置 pendingProject，确保标签立即显示（不依赖 projectName prop 更新）
        const p = projects?.find(pj => pj.id === targetProjectId);
        setPendingProject(
          p
            ? { id: p.id, name: p.name }
            : { id: targetProjectId, name: targetProjectId }
        );
        updateConversationInList(conversationId, conv => ({
          ...conv,
          projectId: targetProjectId,
        }));
        try {
          const { ensureNonCommonSelected } = await import('@/lib/agentUtils');
          ensureNonCommonSelected({ conversationId });
        } catch (e) {
          console.warn('Auto-switch agent after project change failed', e);
        }
      }

      setProjectActionLoading(false);
      setShowAttachmentMenu(false);
      setShowProjectList(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      conversationId,
      projects,
      canChangeProject,
      moveSingleConversation,
      updateConversationInList,
      setShowAttachmentMenu,
      setShowProjectList,
    ]
  );

  const handleRemoveProjectTag = useCallback(() => {
    // 🔥 移除项目时关闭文件预览和 Artifact 分屏
    setFilePreview(null);
    setArtifactViewer(null);

    // 🔥 群聊模式下禁止删除项目
    if (isGroupChat) return;
    // If still pending (new chat selection), just clear locally
    if (!projectId) {
      setPendingProject(null);
      return;
    }
    if (!canChangeProject || !conversationId) return;
    // 显示二次确认弹窗
    setShowRemoveProjectConfirm(true);
  }, [
    projectId,
    canChangeProject,
    conversationId,
    isGroupChat,
    setFilePreview,
    setArtifactViewer,
    setPendingProject,
  ]);

  const confirmRemoveProjectTag = useCallback(async () => {
    if (!conversationId) return;
    setIsRemovingProject(true);
    // 🔥 使用单个对话接口 /ai/conversations/{conversation_id}/move
    const success = await moveSingleConversation(conversationId, null);
    if (success) {
      setLocalProjectId(null); // 🔥 立即更新本地状态，确保 UI 响应
      setPendingProject(null); // 🔥 同时清除 pendingProject，确保标签消失
      updateConversationInList(conversationId, conv => ({
        ...conv,
        projectId: null,
      }));
      try {
        const { ensureNonCommonSelected } = await import('@/lib/agentUtils');
        ensureNonCommonSelected({ conversationId });
      } catch (e) {
        console.warn('Auto-switch agent after removing project failed', e);
      }
    }
    setIsRemovingProject(false);
    setShowRemoveProjectConfirm(false);
    setShowAttachmentMenu(false);
    setShowProjectList(false);
  }, [
    conversationId,
    moveSingleConversation,
    updateConversationInList,
    setLocalProjectId,
    setPendingProject,
    setShowAttachmentMenu,
    setShowProjectList,
  ]);
  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };
  // Handle message change and @ mention detection (for both files and agents)
  const handleMessageChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      const cursorPos = e.target.selectionStart;
      setMessage(newValue);

      const textBefore = newValue.substring(0, cursorPos);
      const atMatch = textBefore.match(/@([^@\s[\]]*)$/);

      if (atMatch) {
        const query = atMatch[1];

        // 🔥 判断是否显示文件下拉框：
        // - 必须有 projectId
        // - 文件已加载完成 (filesIsLoaded)
        // - 有文件可选 (fileTreePaths.length > 0)
        const shouldShowFileDropdown =
          projectId && filesIsLoaded && fileTreePaths.length > 0;

        if (shouldShowFileDropdown) {
          setMentionQuery(query);
          setShowMentionDropdown(true);
          setSelectedMentionIndex(-1);

          // Calculate dropdown position - intelligently place above or below
          const textarea = textareaRef.current;
          if (textarea) {
            const rect = textarea.getBoundingClientRect();
            const dropdownHeight = 200;
            const spaceAbove = rect.top;
            const spaceBelow = window.innerHeight - rect.bottom;

            // Show below if more space below, otherwise show above
            if (spaceBelow > dropdownHeight || spaceBelow > spaceAbove) {
              setMentionPosition({
                top: rect.bottom + 5,
                left: rect.left,
              });
            } else {
              setMentionPosition({
                top: rect.top - dropdownHeight - 5,
                left: rect.left,
              });
            }
          }
        } else {
          // 没有文件可选，隐藏文件下拉框
          setShowMentionDropdown(false);
          setMentionQuery('');
          setSelectedMentionIndex(-1);
        }

        // 🔥 只有群聊时才显示 @Agent 占位符
        // single chat（包括不在任何目录下的）不显示 @Agent 占位符
        if (isGroupChat) {
          const agentPrefix = 'Agent';
          const isAgentPrefix = agentPrefix
            .toLowerCase()
            .startsWith(query.toLowerCase());

          if (isAgentPrefix && query.length < agentPrefix.length) {
            // 显示占位符，补全剩余部分
            setShowAtAgentPlaceholder(true);
            setAtSymbolPosition(cursorPos);
          } else {
            setShowAtAgentPlaceholder(false);
            setAtSymbolPosition(null);
          }
        } else {
          // 非群聊的项目上下文中，隐藏 @Agent 占位符
          setShowAtAgentPlaceholder(false);
          setAtSymbolPosition(null);
        }
      } else {
        setShowMentionDropdown(false);
        setMentionQuery('');
        setSelectedMentionIndex(-1);
        // 🔥 隐藏 @Agent 占位符
        setShowAtAgentPlaceholder(false);
        setAtSymbolPosition(null);
      }
    },
    [projectId, isGroupChat, filesIsLoaded, fileTreePaths.length]
  );

  // 🔥 /skill 命令检测 - 独立处理避免与 @ mention 冲突
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // 检测消息是否以 /skill 开头（或 /skill:xxx）
    const trimmed = message.trimStart();
    const skillMatch = trimmed.match(/^\/skill\s*:?\s*(.*)$/i);

    if (skillMatch) {
      const query = skillMatch[1] || '';
      setSkillQuery(query);
      setShowSkillDropdown(true);
      setSelectedSkillIndex(0);

      // 计算下拉框位置
      const rect = textarea.getBoundingClientRect();
      setSkillAnchorRect(rect);
    } else {
      if (showSkillDropdown) {
        setShowSkillDropdown(false);
        setSkillQuery('');
        setSelectedSkillIndex(0);
      }
    }
  }, [message, showSkillDropdown]);

  // 🔥 Skill 选择处理：填充到输入框（发送时才添加任务）
  const handleSkillSelect = useCallback(
    (_skillId: string, skillName: string) => {
      // 用方括号填充到输入框，如 [skill:TFL]
      setMessage(`[${skillName}] `);
      setShowSkillDropdown(false);
      setSkillQuery('');
      setSelectedSkillIndex(0);

      // 聚焦回输入框，光标移到末尾
      setTimeout(() => {
        const textarea = textareaRef.current;
        if (textarea) {
          textarea.focus();
          const len = `[${skillName}] `.length;
          textarea.setSelectionRange(len, len);
        }
      }, 0);
    },
    []
  );

  // 🔥 计算过滤后的 skill 列表（用于键盘导航）
  const filteredSkillsForNav = useMemo(() => {
    return AVAILABLE_SKILLS.filter(skill => {
      if (!skillQuery) return true;
      const q = skillQuery.toLowerCase();
      return (
        skill.name.toLowerCase().includes(q) ||
        skill.id.toLowerCase().includes(q)
      );
    });
  }, [skillQuery]);
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return `0 ${t('fileSize.bytes')}`;
    const k = 1024;
    const sizes = ['bytes', 'kb', 'mb', 'gb'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (
      parseFloat((bytes / Math.pow(k, i)).toFixed(2)) +
      ' ' +
      t(`fileSize.${sizes[i]}`)
    );
  };

  // Handle pasting images
  const handlePaste = useCallback(
    async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      let hasInvalid = false;
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind !== 'file') continue;
        const file = item.getAsFile();
        if (!file) continue;
        if (!item.type.startsWith('image/')) continue;
        if (!isAllowedImage(file)) {
          hasInvalid = true;
          continue;
        }

        e.preventDefault();
        void uploadImageFile(file, file.name || `pasted-image-${Date.now()}`);
      }
      if (hasInvalid) {
        showToast(t('upload.pasteImagesOnly'), 'error', {
          title: t('chat.imagesOnly'),
        });
      }
    },
    [isAllowedImage, uploadImageFile, t]
  );

  // Remove pasted image
  const removePastedImage = (imageId: string) => {
    setPastedImages(prev => {
      const imgToRemove = prev.find(img => img.id === imageId);
      if (imgToRemove) {
        // 清理缓存的预览 URL
        if (imgToRemove.previewUrl) {
          URL.revokeObjectURL(imgToRemove.previewUrl);
        }
        // 从上传追踪集合中移除
        uploadingFilesRef.current.delete(imgToRemove.file);
      }
      return prev.filter(img => img.id !== imageId);
    });
  };
  return (
    <div className="relative">
      {/* 🔥 任务错误提示（如 message_init 前返回错误） */}
      {/* {taskError && (
        <div className="px-4 pb-2">
          <div className="flex items-center gap-2 bg-red-50 border-l-4 border-red-400 rounded-r-lg px-3 py-2">
            <div className="flex-1 text-sm text-red-700">
              <span className="font-medium">Error: </span>
              {taskError}
            </div>
            {onClearTaskError && (
              <button
                type="button"
                onClick={onClearTaskError}
                className="p-1 text-red-400 hover:text-red-600 hover:bg-red-100 rounded transition-colors flex-shrink-0"
                title={t('chat.dismissError')}
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>
      )} */}
      {/* 🔥 群聊回复预览卡片 */}
      {replyToMessage && isGroupChat && (
        <div className="px-4 pb-2">
          <div className="flex items-start gap-2 bg-gray-100 border-l-4 border-blue-400 rounded-r-lg px-3 py-2 max-w-xl">
            <Reply size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-blue-600 mb-0.5">
                {(replyToMessage as any).user?.name ||
                  (replyToMessage as any).user?.email ||
                  'User'}
              </div>
              <div className="text-sm text-gray-600 line-clamp-2">
                {(() => {
                  const blocks = replyToMessage.choices?.[0]?.blocks || [];
                  return blocks
                    .filter((block: any) => block.type === 'text')
                    .map((block: any) => block.content)
                    .join('\n')
                    .trim()
                    .slice(0, 100);
                })()}
              </div>
            </div>
            <button
              type="button"
              onClick={onClearReply}
              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded transition-colors flex-shrink-0"
              title={t('chat.cancelReply')}
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}
      {/* Attachments */}
      {attachments.length > 0 && (
        <div className={centered ? 'mb-4' : 'p-4 pb-0'}>
          <div
            className={cn(
              'flex flex-wrap gap-2 ',
              !centered && 'max-w-5xl mx-auto'
            )}
          >
            {attachments.map((file, index) => (
              <div
                key={index}
                className="flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-lg text-sm"
              >
                <span className="truncate max-w-32">{file.name}</span>
                <span className="text-gray-500 text-xs">
                  ({formatFileSize(file.size)})
                </span>
                <button
                  onClick={() => removeAttachment(index)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Input container */}
      <div className={centered ? 'relative' : 'py-1 sm:py-4'}>
        <div className={cn(centered ? '' : 'max-w-4xl mx-auto')}>
          <div
            className={cn(
              centered
                ? 'relative'
                : 'border border-gray-300 rounded-2xl shadow-sm overflow-hidden'
            )}
          >
            {/* Input area with inline agent selector */}
            <div
              className={cn(
                'flex flex-col bg-white',
                centered ? 'relative px-4 py-1' : 'px-4 py-1'
              )}
            >
              <div className="relative flex items-end">
                <textarea
                  ref={textareaRef}
                  name="message"
                  value={message}
                  onChange={handleMessageChange}
                  onKeyDown={handleKeyDown}
                  onPaste={handlePaste}
                  placeholder={placeholder}
                  // 当流式返回时允许输入，但不可发送（发送按钮逻辑中处理）
                  disabled={disabled}
                  rows={1}
                  className={cn(
                    'w-full border-0 resize-none focus:outline-none text-base bg-transparent placeholder:text-gray-400 overflow-y-hidden min-h-[36px] sm:min-h-[44px]',
                    centered ? 'px-1 py-3' : 'pt-1 sm:pt-3',
                    disabled && 'opacity-50 cursor-not-allowed',
                    isLoading && 'animate-pulse-soft'
                  )}
                  style={{
                    maxHeight: '160px',
                    wordBreak: 'break-all',
                    overflowWrap: 'break-word',
                  }}
                />
                {/* 🔥 @Agent 灰色占位符 */}
                {showAtAgentPlaceholder && atSymbolPosition !== null && (
                  <div
                    className="absolute pointer-events-none text-gray-400 text-base"
                    style={{
                      top: centered ? '12px' : '12px',
                      left: centered ? '4px' : '0',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                    }}
                  >
                    {/* 渲染消息内容，但把用户输入的 @xxx 后面补全为灰色的 Agent 剩余部分 */}
                    <span style={{ visibility: 'hidden' }}>
                      {message.substring(0, atSymbolPosition)}
                    </span>
                    <span className="text-gray-400">
                      {(() => {
                        // 计算需要补全的部分
                        const textBefore = message.substring(
                          0,
                          atSymbolPosition
                        );
                        const atIndex = textBefore.lastIndexOf('@');
                        if (atIndex === -1) return '';
                        const typed = textBefore.substring(atIndex + 1); // 用户在 @ 后输入的内容
                        const remaining = 'Agent'.substring(typed.length);
                        return remaining;
                      })()}
                    </span>
                  </div>
                )}
              </div>
            </div>
            {/* Controls bar - unified for both centered and non-centered layouts */}
            {agentConfig && agentConfig.length > 0 && (
              <div
                className={cn(
                  'flex items-center justify-between border-t border-gray-100',
                  centered ? 'mt-3 pt-2' : 'px-4 py-3'
                )}
              >
                <DynamicControls config={agentConfig} />
              </div>
            )}
            {/* Bottom button bar */}
            <div
              className={cn(
                'bg-white flex items-center justify-between sm:gap-2 flex-wrap w-full',
                centered
                  ? 'pl-2 pr-4 pb-3'
                  : 'px-2 sm:px-2 pb-1 sm:pb-3 pt-1 sm:pt-2'
              )}
            >
              <div className="min-w-0 flex items-center flex-wrap sm:gap-1">
                <button
                  ref={attachmentButtonRef}
                  type="button"
                  onClick={() => {
                    if (disabled || isLoading) return;
                    const rect =
                      attachmentButtonRef.current?.getBoundingClientRect();
                    if (rect) {
                      const estimatedHeight = 220; // menu + potential list
                      const spaceBelow = window.innerHeight - rect.bottom;
                      const spaceAbove = rect.top;
                      const placeAbove =
                        spaceBelow < estimatedHeight && spaceAbove > spaceBelow;
                      setAttachmentMenuDirection(
                        placeAbove ? 'above' : 'below'
                      );
                      setAttachmentMenuPosition({
                        top: placeAbove ? rect.top - 8 : rect.bottom + 8,
                        left: rect.left,
                      });
                    }
                    setShowAttachmentMenu(prev => !prev);
                    setShowProjectList(false);
                    setShowAgentDropdown(false);
                    setAgentQuery('');
                    setSelectedAgentIndex(-1);
                  }}
                  disabled={disabled || isLoading}
                  className={cn(
                    'p-1 sm:p-1.5 text-gray-500 rounded-lg transition-colors',
                    disabled || isLoading
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:text-gray-700 hover:bg-gray-100',
                    showAttachmentMenu && 'bg-gray-200'
                  )}
                  title={t('chat.addAttachments')}
                >
                  <Plus size={18} className="sm:w-5 sm:h-5" />
                </button>
                {currentView !== 'project-detail' &&
                  !centered &&
                  !isGroupChat && (
                    <button
                      type="button"
                      onClick={() => {
                        if (onNewConversation && !isLoading) {
                          setFilePreview(null); // 🔥 关闭文件预览弹窗
                          onNewConversation();
                        }
                      }}
                      disabled={isLoading}
                      className={cn(
                        'p-1 sm:p-1.5 text-gray-500 rounded-lg transition-colors',
                        isLoading
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:text-gray-700 hover:bg-gray-100'
                      )}
                      title={
                        isLoading
                          ? t('chat.waitForResponse')
                          : t('chat.newChatWithAgent')
                      }
                    >
                      <MessageSquarePlus size={18} className="sm:w-5 sm:h-5" />
                    </button>
                  )}
                {(localProjectId && projectName) || pendingProject ? (
                  <Tooltip
                    content={
                      isLoading ||
                      isGroupChat ||
                      (!!projectId && !canChangeProject) ||
                      projectActionLoading
                        ? ''
                        : `Click to view file list for project  '${projectName || pendingProject?.name}'`
                    }
                    position="top"
                  >
                    <div
                      className={cn(
                        'flex items-center px-1 sm:px-1.5 py-1 text-xs border rounded-sm transition-colors ',
                        isLoading ||
                          isGroupChat ||
                          (!!projectId && !canChangeProject) ||
                          projectActionLoading
                          ? 'text-gray-400 bg-gray-50 border-gray-200 cursor-not-allowed'
                          : 'text-gray-600 bg-blue-50 hover:bg-blue-100 border-blue-200 cursor-pointer'
                      )}
                      onClick={() => {
                        if (isLoading) return;
                        const targetProjectId = projectId || pendingProject?.id;
                        if (targetProjectId && onProjectClick) {
                          onProjectClick(targetProjectId);
                        }
                      }}
                    >
                      <GalleryVerticalEnd size={14} />
                      <span className="hidden sm:inline max-w-16 sm:max-w-20 truncate sm:pl-1">
                        {projectName || pendingProject?.name}
                      </span>
                      <button
                        type="button"
                        onClick={e => {
                          e.stopPropagation();
                          if (!isLoading && !isGroupChat) {
                            handleRemoveProjectTag();
                          }
                        }}
                        disabled={
                          isLoading ||
                          isGroupChat ||
                          (!!projectId && !canChangeProject) ||
                          projectActionLoading
                        }
                        className={cn(
                          'ml-1 rounded-full p-0.5 text-gray-500 hover:text-gray-800 hover:bg-white/80 transition-colors',
                          (isLoading ||
                            isGroupChat ||
                            (!!projectId && !canChangeProject) ||
                            projectActionLoading) &&
                            'opacity-60 cursor-not-allowed'
                        )}
                        title={
                          isLoading
                            ? t('chat.waitForResponse')
                            : isGroupChat
                              ? t('chat.cannotChangeProjectInGroup')
                              : (!!projectId && canChangeProject) || !projectId
                                ? 'Remove project from this conversation'
                                : ''
                        }
                      >
                        {projectActionLoading ? (
                          <svg
                            className="h-3 w-3 animate-spin text-gray-500"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8v2a6 6 0 00-6 6H4z"
                            />
                          </svg>
                        ) : (
                          <X size={12} />
                        )}
                      </button>
                    </div>
                  </Tooltip>
                ) : null}
                {/* 群聊标识 */}
                {isGroupChat && (
                  <div
                    className="flex items-center gap-1.5 px-2.5 py-1 text-xs border rounded-sm text-purple-600 bg-purple-50 border-purple-200"
                    title={t('chat.groupChat')}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M18 21a8 8 0 0 0-16 0" />
                      <circle cx="10" cy="8" r="5" />
                      <path d="M22 20c0-3.37-2-6.5-4-8a5 5 0 0 0-.45-8.3" />
                    </svg>
                    <span>{t('sidebar.groupChats')}</span>
                  </div>
                )}
                {/* 🔥 Stop 按钮：当有 AI 正在输出时显示（群聊和非群聊都显示） */}
                {isLoading && (onStopGroupChat || onStopRequest) && (
                  <button
                    onClick={isGroupChat ? onStopGroupChat : onStopRequest}
                    className="flex items-center gap-1 px-2 py-1 text-xs border rounded-sm text-red-600 bg-red-50 border-red-200 hover:bg-red-100 transition-colors"
                    title={t('chat.stopAIResponse')}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      stroke="none"
                    >
                      <rect x="4" y="4" width="16" height="16" rx="2" />
                    </svg>
                    <span>{t('chat.stopGenerating')}</span>
                  </button>
                )}
              </div>
              <div className="pointer-events-none flex items-center flex-wrap gap-1 flex-shrink-0">
                <div
                  className={cn(
                    'pointer-events-auto inline-flex items-center gap-1 flex-1 rounded-xl bg-white/90 px-0 py-1.5 text-sm text-gray-700',
                    // borderless like reference
                    'hover:bg-white'
                  )}
                >
                  <button
                    ref={agentButtonRef}
                    type="button"
                    onClick={() => {
                      if (!isLoading) {
                        setShowAttachmentMenu(false);
                        setShowProjectList(false);
                        setShowAgentDropdown(prev => !prev);
                        setAgentQuery('');
                        setSelectedAgentIndex(-1);
                      }
                    }}
                    disabled={isLoading}
                    className={cn(
                      'inline-flex items-center gap-2 rounded-lg bg-transparent p-1',
                      isLoading ? 'opacity-50 cursor-not-allowed' : ''
                    )}
                    title={
                      isLoading
                        ? t('chat.cannotChangeAgent')
                        : t('chat.chooseAgent')
                    }
                  >
                    {displayAgent ? (
                      <AgentIcon
                        agent={displayAgent as Agent}
                        className="w-5 h-5 rounded object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="flex h-5 w-5 items-center justify-center rounded bg-gray-100 text-[10px] font-semibold text-gray-500">
                        AI
                      </div>
                    )}
                    <span className="hidden sm:inline max-w-[80px] truncate text-xs sm:text-sm">
                      {displayAgent?.name || t('chat.selectModelLabel')}
                    </span>
                    <ChevronDown
                      size={16}
                      className="hidden sm:inline text-gray-500"
                    />
                  </button>
                </div>

                {/* Model Selection Dropdown */}
                <div
                  className={cn(
                    'pointer-events-auto inline-flex items-center gap-1 flex-1 rounded-xl bg-white/90 px-2 pl-0 py-1.5 text-sm text-gray-700',
                    'hover:bg-white ml-2'
                  )}
                >
                  <button
                    ref={modelButtonRef}
                    type="button"
                    onClick={() => {
                      if (!isLoading) {
                        setShowAttachmentMenu(false);
                        setShowProjectList(false);
                        setShowAgentDropdown(false);
                        setShowModelDropdown(prev => !prev);
                      }
                    }}
                    disabled={isLoading || modelsLoading}
                    className={cn(
                      'inline-flex items-center gap-2 rounded-lg bg-transparent p-1',
                      isLoading || modelsLoading
                        ? 'opacity-50 cursor-not-allowed'
                        : ''
                    )}
                    title={
                      modelsLoading
                        ? t('chat.loadingModels')
                        : isLoading
                          ? t('chat.cannotChangeModel')
                          : t('chat.chooseModel')
                    }
                  >
                    <div className="flex h-5 w-5 items-center justify-center rounded bg-purple-100 text-[10px] font-semibold text-purple-600">
                      M
                    </div>
                    <span className="hidden sm:inline max-w-[100px] truncate text-xs sm:text-sm">
                      {modelsLoading
                        ? t('chat.loading')
                        : selectedModel || t('chat.selectModel')}
                    </span>
                    <ChevronDown
                      size={16}
                      className="hidden sm:inline text-gray-500"
                    />
                  </button>
                </div>

                <button
                  onClick={handleSubmit}
                  disabled={pastedImages.some(
                    img => img.isUploading || img.error
                  )}
                  className={cn(
                    'pointer-events-auto p-1 mr-0 text-white rounded-lg transition-colors flex-shrink-0',
                    message.trim() &&
                      !pastedImages.some(img => img.isUploading || img.error)
                      ? 'bg-claude-orange hover:bg-orange-600 cursor-pointer'
                      : 'bg-gray-300 cursor-not-allowed',
                    currentView === 'project-detail' ? 'mr-0' : 'mr-2'
                  )}
                >
                  <ArrowUp size={20} />
                </button>
              </div>
            </div>
            {/* Divider before pasted images */}
            {pastedImages.length > 0 && (
              <div className="border-t border-gray-200"></div>
            )}
            {/* Pasted Images Container - below bottom button bar */}
            {pastedImages.length > 0 && (
              <div className={cn(' bg-claude-bg', centered ? '' : 'mx-4')}>
                <div
                  className={cn(
                    'flex flex-wrap gap-3 p-3',
                    !centered && 'max-w-5xl'
                  )}
                >
                  {pastedImages.map(img => (
                    <div key={img.id} className="relative group">
                      <div
                        className="flex items-center justify-center w-20 h-20 bg-white rounded-lg overflow-hidden border border-gray-200 cursor-pointer hover:opacity-75 transition-opacity"
                        onClick={() =>
                          img.previewUrl &&
                          setPreviewImage({
                            url: img.previewUrl,
                            name: img.name,
                          })
                        }
                      >
                        {img.isUploading ? (
                          <div className="text-center text-xs text-gray-500">
                            <div className="animate-spin mb-1">⏳</div>
                            <span className="text-xs">
                              {t('upload.uploading')}
                            </span>
                          </div>
                        ) : img.error ? (
                          <div className="text-center text-xs text-red-500">
                            <span>{t('common.error')}!</span>
                          </div>
                        ) : (
                          <img
                            src={img.previewUrl}
                            alt={img.name}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      {/* 删除按钮：上传完成或错误时均可删除 */}
                      {!img.isUploading && (
                        <div className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => removePastedImage(img.id)}
                            className="bg-red-500 hover:bg-red-600 text-white rounded-full p-0.5 shadow-md"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileSelect}
        className="hidden"
        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
      />
      {showAttachmentMenu && (
        <div
          ref={attachmentMenuRef}
          className="fixed z-50 w-64 rounded-xl border border-gray-200 bg-white shadow-xl"
          style={{
            top:
              attachmentMenuDirection === 'below'
                ? `${attachmentMenuPosition.top}px`
                : undefined,
            bottom:
              attachmentMenuDirection === 'above'
                ? `${window.innerHeight - attachmentMenuPosition.top}px`
                : undefined,
            left: `${attachmentMenuPosition.left}px`,
          }}
        >
          <div className="py-2">
            <button
              className="flex w-full items-center gap-3 px-3 py-2 text-sm text-gray-800 hover:bg-gray-50"
              onClick={handleUploadClick}
            >
              <Paperclip size={16} className="text-gray-500" />
              <span className="flex-1 text-left">{t('chat.uploadFile')}</span>
            </button>
            {/* 🔥 Star/Unstar 按钮 - 只在已有对话时显示 */}
            {conversationId && (
              <button
                className="flex w-full items-center gap-3 px-3 py-2 text-sm text-gray-800 hover:bg-gray-50"
                onClick={async () => {
                  if (!conversationId) return;
                  try {
                    const success = await toggleStar(conversationId, isStarred);
                    if (success) {
                      onStarredChange?.(!isStarred);
                      // 刷新侧边栏的 starred 列表
                      window.refreshSidebarStarred?.();
                      // 🔥 更新 Recent chats 中的 starred 状态
                      window.updateConversationStarred?.(
                        conversationId,
                        !isStarred
                      );
                    }
                  } catch (err) {
                    console.error('Failed to toggle star:', err);
                  }
                  setShowAttachmentMenu(false);
                }}
              >
                <Star
                  size={16}
                  className="text-yellow-500"
                  fill={isStarred ? 'currentColor' : 'none'}
                />
                <span className="flex-1 text-left">
                  {isStarred ? t('chat.unstar') : t('chat.star')}
                </span>
              </button>
            )}
            {currentView !== 'project-detail' && !isGroupChat && (
              <div className="relative">
                <button
                  className={cn(
                    'flex w-full items-center gap-3 px-3 py-2 text-sm text-gray-800 hover:bg-gray-50'
                  )}
                  onClick={() => {
                    setShowProjectList(prev => !prev);
                  }}
                >
                  <GalleryVerticalEnd size={16} className="text-gray-500" />
                  <span className="flex-1 text-left">
                    {t('chat.useProject')}
                  </span>
                  <ChevronRight size={16} className="text-gray-400" />
                </button>
                {showProjectList && (
                  <div
                    ref={projectListRef}
                    className="mt-1 max-h-60 overflow-y-auto border-t border-gray-100"
                  >
                    {projects &&
                    projects.filter(
                      p =>
                        p.role !== 'viewer' && !p.archived && p.id !== projectId
                    ).length > 0 ? (
                      projects
                        .filter(
                          p =>
                            p.role !== 'viewer' &&
                            !p.archived &&
                            p.id !== projectId
                        )
                        .map(project => (
                          <button
                            key={project.id}
                            className={cn(
                              'flex w-full items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50',
                              (projectId === project.id ||
                                pendingProject?.id === project.id) &&
                                'bg-blue-50 text-blue-700'
                            )}
                            onClick={() => handleProjectSelect(project.id)}
                            disabled={projectActionLoading}
                          >
                            <span className="flex-1 truncate">
                              {project.name}
                            </span>
                            {(projectId === project.id ||
                              pendingProject?.id === project.id) && (
                              <Check size={14} />
                            )}
                          </button>
                        ))
                    ) : (
                      <div className="px-3 py-2 text-sm text-gray-500">
                        {hasLoadedProjects
                          ? t('chat.noProjectsAvailable')
                          : t('chat.loadingProjects')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Model Selection Dropdown */}
      {showModelDropdown &&
        models.length > 0 &&
        (() => {
          const buttonRect = modelButtonRef.current?.getBoundingClientRect();
          const dropdownHeight = 320;
          const spaceAbove = buttonRect ? buttonRect.top : 0;
          const spaceBelow = buttonRect
            ? window.innerHeight - buttonRect.bottom
            : 0;
          const showAbove =
            spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

          return (
            <div
              ref={modelListRef}
              className="fixed z-50 bg-white rounded-lg shadow-lg overflow-y-auto"
              style={{
                [showAbove ? 'bottom' : 'top']: showAbove
                  ? `${window.innerHeight - (buttonRect?.top || 0) + 8}px`
                  : `${(buttonRect?.bottom || 0) + 8}px`,
                left: `${Math.min(buttonRect?.left || 0, window.innerWidth - Math.min(320, window.innerWidth - 16))}px`,
                width: `${Math.min(320, window.innerWidth - 16)}px`,
                maxHeight: '320px',
              }}
            >
              {models.map(model => (
                <div
                  key={model.name}
                  className={cn(
                    'px-3 py-2 cursor-pointer border-b border-gray-100 last:border-b-0 hover:bg-gray-50',
                    model.name === selectedModel && 'bg-blue-50'
                  )}
                  onClick={() => handleModelSelect(model.name)}
                  title={model.description}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-gray-900">
                        {model.name}
                      </div>
                      {model.description && (
                        <div
                          className="text-xs text-gray-500"
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {model.description}
                        </div>
                      )}
                    </div>
                    {model.name === selectedModel && (
                      <Check
                        size={16}
                        className="text-blue-600 flex-shrink-0"
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          );
        })()}

      {/* @ Agent Mention Dropdown */}
      {showAgentDropdown &&
        filteredAgents.length > 0 &&
        (() => {
          const buttonRect = agentButtonRef.current?.getBoundingClientRect();
          const dropdownHeight = 320;
          const spaceAbove = buttonRect ? buttonRect.top : 0;
          const spaceBelow = buttonRect
            ? window.innerHeight - buttonRect.bottom
            : 0;
          const showAbove =
            spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

          return (
            <div
              ref={agentListRef}
              className="fixed z-50 bg-white rounded-lg shadow-lg overflow-y-auto"
              style={{
                [showAbove ? 'bottom' : 'top']: showAbove
                  ? `${window.innerHeight - (buttonRect?.top || 0) + 8}px`
                  : `${(buttonRect?.bottom || 0) + 8}px`,
                left: `${Math.min(buttonRect?.left || 0, window.innerWidth - Math.min(320, window.innerWidth - 16))}px`,
                width: `${Math.min(320, window.innerWidth - 16)}px`,
                maxHeight: '320px',
              }}
            >
              {dropdownAgents.map((agent, index) => (
                <div
                  key={agent.id}
                  className={cn(
                    'px-3 py-2 cursor-pointer border-b border-gray-100 last:border-b-0 flex items-center gap-3',
                    index === selectedAgentIndex
                      ? 'bg-blue-50'
                      : 'hover:bg-gray-50'
                  )}
                  title={
                    agent.description
                      ? `${agent.name}: ${agent.description}`
                      : agent.name
                  }
                  onClick={() => handleAgentSelect(agent)}
                  onMouseEnter={() => setSelectedAgentIndex(index)}
                >
                  <AgentIcon agent={agent} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {agent.name}
                      </div>
                      {displayAgent?.id === agent.id && (
                        <Check size={16} className="text-claude-orange" />
                      )}
                    </div>
                    {agent.description && (
                      <div className="text-xs text-gray-500 truncate">
                        {agent.description}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {filteredAgents.length > 5 && !showAllAgents && (
                <button
                  className="w-full px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
                  onClick={() => setShowAllAgents(true)}
                >
                  More agents
                </button>
              )}
            </div>
          );
        })()}
      {/* 🔥 /skill Dropdown */}
      <SkillDropdown
        ref={skillDropdownRef}
        show={showSkillDropdown}
        selectedIndex={selectedSkillIndex}
        query={skillQuery}
        anchorRect={skillAnchorRect}
        onSelect={handleSkillSelect}
        onMouseEnter={setSelectedSkillIndex}
        direction={currentView === 'project-detail' ? 'down' : 'up'}
      />

      {/* @ File Mention Dropdown */}
      {showMentionDropdown && (
        <div
          ref={mentionListRef}
          className="fixed z-50 bg-white rounded-lg shadow-lg overflow-y-auto"
          style={{
            top: mentionPosition.top,
            left: mentionPosition.left,
            width: '400px',
            height: `${CONTAINER_HEIGHT}px`,
          }}
          onScroll={handleMentionScroll}
        >
          {allFilteredFiles.length === 0 ? (
            <div className="px-3 py-4 text-sm text-gray-500 text-center">
              No files found
            </div>
          ) : (
            <>
              {/* Virtual scrolling container */}
              <div
                style={{
                  height: `${virtualScrollData.totalHeight}px`,
                  position: 'relative',
                }}
              >
                {/* Visible items container */}
                <div
                  style={{
                    transform: `translateY(${virtualScrollData.offsetY}px)`,
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                  }}
                >
                  {virtualScrollData.visibleItems.map((file, index) => {
                    const actualIndex = virtualScrollData.startIndex + index;
                    return (
                      <div
                        key={`${file}-${actualIndex}`}
                        className={cn(
                          'px-3 py-2 text-sm cursor-pointer border-b border-gray-100',
                          actualIndex === selectedMentionIndex
                            ? 'bg-blue-50 text-blue-700'
                            : 'text-gray-700 hover:bg-gray-50'
                        )}
                        onClick={() => handleMentionSelect(file)}
                        onMouseEnter={() =>
                          setSelectedMentionIndex(actualIndex)
                        }
                        style={{
                          height: `${ITEM_HEIGHT}px`,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <div className="truncate" title={file}>
                          {file}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      )}
      {/* 🔥 图片预览 Lightbox — 支持缩放、上一张/下一张导航 */}
      {previewImage && (
        <ImageLightbox
          images={[{ src: previewImage.url, alt: previewImage.name }]}
          currentIndex={0}
          onClose={() => setPreviewImage(null)}
        />
      )}
      {currentView === 'chat' && (
        <p
          className={`text-xs text-gray-400 text-center ${
            centered
              ? 'lg:hidden fixed bottom-14 left-0 right-0 mb-3'
              : 'hidden lg:block mt-1'
          }`}
        >
          {t('chat.disclaimer')}
        </p>
      )}

      {/* Remove from Project Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showRemoveProjectConfirm}
        title={t('chat.removeFromProject')}
        message={t('confirmDialog.removeFromProjectConfirm', { count: 1 })}
        confirmText={t('common.confirm')}
        cancelText={t('common.cancel')}
        type="warning"
        isLoading={isRemovingProject}
        onConfirm={confirmRemoveProjectTag}
        onCancel={() => {
          if (isRemovingProject) return;
          setShowRemoveProjectConfirm(false);
        }}
      />
    </div>
  );
}
